import { Observable, of, catchError, map, tap, switchMap } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { LocationEventDTO } from '../../../../dto/event/location-event.dto';
import { ListResponseDTO } from '../../../../dto/list-response.dto';
import { ListRequestDTO } from '../../../../dto/list-request.dto';
import { Router } from '@angular/router';

function getEventDisplayName(event: LocationEventDTO): string {
  if (event.event_description?.short_description) {
    const shortDesc = event.event_description.short_description;
    const danishKey = Object.keys(shortDesc).find(k => shortDesc[k]?.language_code === 'da');
    const englishKey = Object.keys(shortDesc).find(k => shortDesc[k]?.language_code === 'en');
    if (danishKey && shortDesc[danishKey]?.translation_text) {
      return shortDesc[danishKey].translation_text;
    }
    if (englishKey && shortDesc[englishKey]?.translation_text) {
      return shortDesc[englishKey].translation_text;
    }
    if (Object.keys(shortDesc).length > 0) {
      const firstKey = Object.keys(shortDesc)[0];
      if (shortDesc[firstKey]?.translation_text) {
        return shortDesc[firstKey].translation_text;
      }
    }
  }
  return event.title || 'Untitled Event';
}

export function location_events(service: ComponentDataSourceService) {
  const router = service.injector.get(Router);

  let groupsFeatureEnabled = false;
  const groupEventsTableOptions: {
    id: string;
    label: string;
    type: string;
    default: boolean;
    onChange: () => void;
  }[] = [];

  function buildTableSettings() {
    const tableOptions: unknown[] = [
      {
        id: 'hidePastEvents',
        label: 'Skjul afsluttede events',
        type: 'toggle',
        default: true,
        onChange: () => {}
      }
    ];
    if (groupsFeatureEnabled) {
      tableOptions.push({
        id: 'showGroupEvents',
        label: 'Vis gruppe-events',
        type: 'toggle',
        default: false,
        onChange: () => {}
      });
    }

    return {
      columns: [
        { key: 'displayName', header: 'Navn', type: 'text' },
        { key: 'formatted_date', header: 'Dato', type: 'text' },
        {
          key: 'total_participants',
          header: 'Pladser',
          type: 'text',
          sortable: true,
          alignment: 'center',
          transform: (_value: number, row?: LocationEventDTO) => {
            const taken = row?.total_participants ?? 0;
            const capacity = row?.capacity ?? 0;
            return capacity > 0 ? `${taken}/${capacity}` : String(taken);
          }
        },
        { key: 'waitlist_count', header: 'Venteliste', type: 'number' },
        {
          key: 'attended_count',
          header: 'Fremmøde',
          type: 'text',
          sortable: true,
          alignment: 'center',
          transform: (_value: number, row?: LocationEventDTO) => {
            const attended = row?.attended_count ?? 0;
            const total = row?.total_participants ?? 0;
            return `${attended}/${total}`;
          }
        },
        {
          key: 'actions',
          header: 'Handlinger',
          type: 'button',
          buttonText: 'Vis',
          buttonColor: 'primary',
          alignment: 'right',
          variant: 'raised',
          action: (row: { guid: string }) => {
            router.navigate(['/event', row.guid]);
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'start_date',
      defaultSortDirection: 'asc',
      tableOptions
    };
  }

  function fetchLocationEvents(params: LoadDataParams | undefined): Observable<any> {
    let locationUuid: string | undefined;

    if (params?.filters && Array.isArray(params.filters)) {
      const locationUuidFilter = params.filters.find(f => f.column === 'locationUuid');
      if (locationUuidFilter?.value) {
        locationUuid = locationUuidFilter.value;
      }
    }

    if (!locationUuid && params?.queryParams?.['locationUuid']) {
      locationUuid = params.queryParams['locationUuid'];
    }

    if (!locationUuid) {
      const segments = router.url.split('/');
      const locationIndex = segments.indexOf('lokation');
      if (locationIndex !== -1 && locationIndex < segments.length - 1) {
        locationUuid = segments[locationIndex + 1];
      } else {
        const altLocationIndex = segments.indexOf('locations');
        if (altLocationIndex !== -1 && altLocationIndex < segments.length - 1) {
          locationUuid = segments[altLocationIndex + 1];
        }
      }
    }

    if (!locationUuid) {
      return of({ items: [], totalCount: 0, error: 'Missing locationUuid parameter' });
    }

    const hidePastFilter = params?.filters?.find(f => f.column === 'hidePastEvents');
    const hidePastEvents = hidePastFilter !== undefined ? Boolean(hidePastFilter.value) : true;

    const showGroupFilter = params?.filters?.find(f => f.column === 'showGroupEvents');
    const showGroupEvents = showGroupFilter !== undefined ? Boolean(showGroupFilter.value) : false;

    const request: ListRequestDTO = {
      page: params?.queryParams?.['page'] ? Number(params.queryParams['page']) : 1,
      pageSize: params?.queryParams?.['pageSize'] ? Number(params.queryParams['pageSize']) : 10,
      sortBy: params?.sort?.active || 'start_date',
      sortDir: (params?.sort?.direction as 'asc' | 'desc') || 'asc',
      search: params?.searchQuery || '',
      filters: {
        hidePastEvents,
        showGroupEvents
      }
    };

    if (params?.filters && Array.isArray(params.filters)) {
      params.filters.forEach(filter => {
        if (
          filter.column !== 'locationUuid' &&
          filter.column !== 'hidePastEvents' &&
          filter.column !== 'showGroupEvents' &&
          filter.value !== undefined
        ) {
          if (!request.filters) request.filters = {};
          request.filters[filter.column] = filter.value;
        }
      });
    }

    return service.http.post<ListResponseDTO<LocationEventDTO>>(
      `${service.apiUrl}/locations/${locationUuid}/events`,
      request
    ).pipe(
      map(response => {
        let eventItems: LocationEventDTO[] = [];
        if (response?.data && Array.isArray(response.data)) {
          eventItems = response.data;
        } else if (Array.isArray(response)) {
          eventItems = response;
        } else {
          return { items: [], totalCount: 0, error: 'Unexpected response format' };
        }

        eventItems = eventItems.filter((event: LocationEventDTO & { deleted?: boolean; is_active?: boolean }) =>
          event.deleted !== true && event.is_active !== false
        );

        const formattedItems = eventItems.map((event: LocationEventDTO) => ({
          ...event,
          displayName: getEventDisplayName(event),
          actions: [
            {
              type: 'button',
              icon: 'visibility',
              tooltip: 'View Event',
              click: () => router.navigate(['/event', event.guid])
            }
          ]
        }));

        return {
          items: formattedItems,
          totalCount: response.meta?.totalItems || formattedItems.length,
          pageSize: response.meta?.pageSize || 10,
          pageIndex: response.meta ? response.meta.page - 1 : 0,
          settings: buildTableSettings()
        };
      }),
      catchError(error => of({
        items: [],
        totalCount: 0,
        error: error.message || 'Error fetching events'
      }))
    );
  }

  return {
    name: 'Location Events',
    description: 'Events at a specific location',
    execute: (params?: LoadDataParams): Observable<any> => {
      const groupsObs = service.executeDataSource('duna_groups_enabled', {});
      if (!groupsObs) {
        groupsFeatureEnabled = false;
        return fetchLocationEvents(params);
      }

      return groupsObs.pipe(
        tap((res: { visible?: boolean }) => {
          groupsFeatureEnabled = res?.visible === true;
        }),
        switchMap(() => fetchLocationEvents(params))
      );
    },
    settings: buildTableSettings()
  };
}
